import { StudentGroup, StudentGroupDataQuery, StudentStore } from "Stores/studentStore";
import { AppStore } from "Stores/appStore";
import React, { useState, useMemo, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Defaults, Utils } from "../../common/utils";
import { Button, Col, Modal, Row, Spinner } from "react-bootstrap";
import { inject, observer } from "mobx-react";
import { CellContext, ColumnDef, PaginationState, Row as RowInfo, SortingState } from "@tanstack/react-table";

import { StudentGroupForm } from "Components/Students/StudentGroupForm";
import { Student } from "Stores/commonStore";
import { SimpleCheckbox } from "Components/Common/SimpleCheckbox";
import DebouncedInput from "Components/Common/DebouncedInput";
import { StyledTable } from "Components/Common/StyledTable";
import { EditIcon } from "Components/Icons/EditIcon";
import { DeleteIcon } from "Components/Icons/DeleteIcon";
import PageTitle from "Components/Common/PageTitle";

interface StudentGroupDeleteConfirmationMessageProps {
    group: StudentGroup;
    studentStore?: StudentStore;
    appStore?: AppStore;
}

interface StudentGroupDeleteConfirmationMessageState {
    studentsAssociatedTotal: number;
}

@inject("studentStore", "appStore")
@observer
class StudentGroupDeleteConfirmationMessage extends React.Component<StudentGroupDeleteConfirmationMessageProps, StudentGroupDeleteConfirmationMessageState> {
    state = {
        studentsAssociatedTotal: 0,
    };

    componentDidMount() {
        if (!this.props.studentStore) {
            return null;
        }
        this.props.studentStore.fetchStudentsForGroup(this.props.group.ruuid).then((students: Student[]) => {
            if (!students || !students.length) {
                return null;
            }
            this.setState({ studentsAssociatedTotal: students.length });
        });
    }

    renderDeleteBaseMessage = (name: string) => {
        return <p>{`Are you sure you want to delete “${name}”?`}</p>;
    };

    render() {
        if (this.state.studentsAssociatedTotal === 0) {
            return <div className={"text-center"}>{this.renderDeleteBaseMessage(this.props.group.name)}</div>;
        }

        return (
            <div className={"text-center"}>
                {this.renderDeleteBaseMessage(this.props.group.name)}
                <p>This group is associated with {this.state.studentsAssociatedTotal} students</p>
            </div>
        );
    }
}

interface StudentGroupCreateModalProps {
    onSubmit: (group: StudentGroup, students: Student[], users: string[]) => void;
    handleCloseModal: () => void;
    groupType?: string;
}

interface StudentGroupCreateModalState {
    group?: StudentGroup;
    students?: Student[];
    users?: string[];
}

class StudentGroupCreateModal extends React.Component<StudentGroupCreateModalProps, StudentGroupCreateModalState> {
    handleModalDismiss = () => {
        this.props.handleCloseModal();
    };

    isValid = (): boolean => {
        if (!this.state || !this.state.group || !this.state.students) {
            return false;
        }
        const name = this.state.group.name.trim();
        return name.length > 0;
    };

    handleCreate = () => {
        if (this.state.group && this.state.students && this.state.users && this.isValid()) {
            this.props.onSubmit(this.state.group, this.state.students, this.state.users);
        }
    };

    handleOnChange = (group: StudentGroup, students: Student[], users: string[]) => {
        this.setState({
            group,
            students,
            users,
        });
    };

    render() {
        return (
            <Modal id="user-modal-form" centered scrollable show={true} onHide={this.handleModalDismiss} dialogClassName="modal-container wide">
                <Modal.Header>
                    <Row>
                        <Col md={12} className="text-lg-start">
                            <span className="modal-title">Create Student Group</span>
                        </Col>
                    </Row>
                </Modal.Header>
                <Modal.Body>
                    <StudentGroupForm onChange={this.handleOnChange} onCancel={this.handleModalDismiss} theme="light" groupType={this.props.groupType} />
                </Modal.Body>
                <Modal.Footer>
                    <div className="button-group">
                        <Button variant="outline" onClick={this.handleModalDismiss}>
                            Cancel
                        </Button>
                        <Button variant="secondary" onClick={this.handleCreate} disabled={!this.isValid()}>
                            Save
                        </Button>
                    </div>
                </Modal.Footer>
            </Modal>
        );
    }
}

interface StudentGroupsProps {
    studentStore?: StudentStore;
    appStore?: AppStore;
    groupType?: string;
}

interface StudentGroupsState {
    loading: boolean;
    pageSize: number;
}

@inject("studentStore", "appStore")
@observer
export class StudentGroups extends React.Component<StudentGroupsProps, StudentGroupsState> {
    state = {
        loading: false,
        pageSize: Defaults.tablePageSize(),
    };

    componentDidMount() {
        Utils.setViewName("StudentGroupList", "Student Groups");
    }

    render() {
        return (
            <>
                <PageTitle pageTitle={"Student Groups"} />
                <GroupsListReactTable studentStore={this.props.studentStore} appStore={this.props.appStore} groupType={this.props.groupType} />
            </>
        );
    }
}

const getPageCount = (totalCount: number, pageSize: number): number => {
    return Math.ceil(totalCount / pageSize) || 0;
};

interface GroupsListReactTableProps {
    studentStore?: StudentStore;
    appStore?: AppStore;
    groupType?: string;
}

interface IQueryParams {
    query: string;
    groupId: string;
    page: string;
    pageSize: string;
    is_active: string;
}

function GroupsListReactTable({ studentStore, appStore, groupType }: GroupsListReactTableProps) {
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [sorting, setSorting] = useState<SortingState>([]);
    const [searchBarValue, setSearchBarValue] = useState("");

    const [searchParams, setSearchParams] = useSearchParams();
    const [refreshKey, setRefreshKey] = useState(0);
    const [dataQuery, setDataQuery] = React.useState<StudentGroupDataQuery | null>(null);
    const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
        pageIndex: searchParams.get("page") ? Math.max(0, parseInt(searchParams.get("page") as string) - 1) : 0,
        pageSize: parseInt(searchParams.get("pageSize") as string) || 10,
    });

    const [expanded, setExpanded] = useState({});

    const groupId = searchParams.get("groupId");

    const clearQueryParams = (params: URLSearchParams, options?: { keepPagination?: boolean }) => {
        params.delete("query");
        params.delete("groupId");
        params.delete("is_active");

        if (!options?.keepPagination) {
            params.delete("page");
            params.delete("pageSize");
        }
    };

    const addQueryParams = (params: URLSearchParams, item: Partial<IQueryParams>) => {
        if(item.query) params.set("query", item.query);
        if(item.groupId) params.set("groupId", item.groupId);
        if(item.is_active) params.set("is_active", item.is_active);
        if(item.page) params.set("page", item.page);
        if(item.pageSize) params.set("pageSize", item.pageSize);
    };

    const onSearchList = (value: string) => {
        setPagination((prev) => ({ ...prev, pageIndex: 0 }));
        setSearchBarValue(value.toString());

        setSearchParams((searchParams) => {
            if (searchParams.get("groupId") || searchParams.get("page") || searchParams.get("pageSize")) {
                clearQueryParams(searchParams);
            }

            if (value.trim()) {
                addQueryParams(searchParams, {
                    query: value.toString(),
                });
            } else {
                clearQueryParams(searchParams);
            }
            return searchParams;
        });
    };

    const expandRowByGroupId = (groupId: string) => {
        if (dataQuery?.data) {
            const rowIndex = dataQuery.data.findIndex((row) => row.ruuid === groupId);
            if (rowIndex !== -1) {
                setExpanded({
                    [rowIndex]: true,
                });
            }
        }
    };

    const onEditToggleExpand = (row: RowInfo<StudentGroup>) => {
        row.toggleExpanded();

        setSearchParams((searchParams) => {
            const newParams = new URLSearchParams(searchParams);
            if (!row.getIsExpanded()) {
                addQueryParams(newParams, {
                    groupId: row.original.ruuid,
                    is_active: row.original.is_active.toString()
                });
            } else {
                clearQueryParams(newParams, { keepPagination: true });
            }

            return newParams;
        });
    };

    const columns = useMemo<ColumnDef<StudentGroup>[]>(
        () => [
            {
                header: "Active",
                accessorKey: "is_active",
                size: 100,
                cell: (props) => <SimpleCheckbox id={`student-group-is-active-${props.row.original.ruuid}`} isChecked={props.row.original.is_active} readOnly={true} />,
            },
            {
                header: "Name",
                accessorKey: "name",
            },
            {
                header: "Description",
                accessorKey: "description",
            },
            {
                id: "expander",
                header: () => null,
                enableColumnFilter: false,
                size: 35,
                cell: (props) => {
                    return (
                        <div>
                            <button className="table-action-button" onClick={() => onEditToggleExpand(props.row)}>
                                <EditIcon className="edit-group-icon" />
                            </button>
                        </div>
                    );
                },
            },
            {
                id: "deleter",
                header: () => null,
                size: 35,
                enableColumnFilter: false,
                cell: (props: CellContext<StudentGroup, unknown>) => {
                    const onClick = () => {
                        handleDelete(props.row.original);
                    };

                    return (
                        <div>
                            <button className="table-action-button" onClick={onClick}>
                                <DeleteIcon className="delete-group-icon" />
                            </button>
                        </div>
                    );
                },
            },
        ],
        []
    );

    async function fetchStudentGroups() {
        if (!studentStore) {
            return;
        }

        let sortBy;
        let sortOrder;
        if (sorting.length > 0) {
            sortBy = sorting[0].id;
            sortOrder = sorting[0].desc ? "desc" : "asc";
        }

        const filters = {};

        if (searchBarValue) {
            filters["search_term"] = {
                operator: "contains",
                value: `${searchBarValue}`,
            };
        }

        await studentStore.fetchStudentGroups({ page: pageIndex + 1, pageSize, sortBy, sortOrder, filters });
        setDataQuery({
            data: studentStore.studentGroups,
            result: { totalCount: studentStore.totalStudentGroups },
        });
    }

    const onSetCurrentPageNumber = (e: number, pageSize: number) => {
        setPagination({ pageIndex: e++, pageSize });

        if (expanded) {
            setExpanded({});
        }

        setSearchParams((searchParams) => {
            const newParams = new URLSearchParams(searchParams);

            if (groupId) {
                newParams.delete("groupId");
                newParams.delete("is_active");
            }

            addQueryParams(newParams, {
                page: (e++).toString(),
                pageSize: pageSize.toString()
            });

            return newParams;
        });
    };

    useEffect(() => {
        fetchStudentGroups().catch(() => {});
    }, [refreshKey, pageIndex, pageSize, sorting, searchBarValue]);

    useEffect(() => {
        if (groupId && dataQuery) {
            expandRowByGroupId(groupId);
        }
    }, [groupId, dataQuery]);

    useEffect(() => {
        setSearchParams((searchParams) => {
            const newParams = new URLSearchParams(searchParams);
            const page = pageIndex + 1;

            addQueryParams(newParams, {
                page: (page).toString(),
                pageSize: pageSize.toString(),
            });

            return newParams;
        });
    }, [pageIndex, pageSize, searchParams]);

    const handleDelete = (group: StudentGroup) => {
        if (!appStore) {
            return;
        }
        appStore.showConfirmModal({
            element: <StudentGroupDeleteConfirmationMessage group={group} />,
            accept: () => deleteStudentGroup(group.ruuid),
            cancel: () => {},
            showActions: true,
        });
    };

    const deleteStudentGroup = async (id: string) => {
        if (!studentStore) {
            return;
        }
        await studentStore.deleteStudentGroup(id);
        setRefreshKey((prevState) => prevState + 1);
    };

    const handleSave = async (group: StudentGroup, students: Student[], users: string[]) => {
        if (!studentStore) return;

        try {
            await studentStore.createStudentGroup(group, students, users);
            setShowCreateModal(false);
            setRefreshKey((prevState) => prevState + 1);
        } catch (e) {
            appStore?.addError(e);
        }
    };

    const handleModalClose = () => {
        setShowCreateModal(false);
    };

    const openCreateModal = () => {
        setShowCreateModal(true);
    };

    const getEditHandler = (row: RowInfo<StudentGroup>) => {
        return async (group: StudentGroup, students: Student[], users: string[]) => {
            if (!studentStore) {
                return;
            }
            try {
                await studentStore.updateStudentGroup(group.ruuid, group, students, users);
                setRefreshKey((prevState) => prevState + 1);
                row.toggleExpanded();
            } catch (e) {
                appStore?.addError(e);
            }
        };
    };

    const renderSubComponent = ({ row }: { row: RowInfo<StudentGroup> }) => {
        return (
            <div className={"student-group-form-container"}>
                <StudentGroupForm onSubmit={getEditHandler(row)} onCancel={() => onEditToggleExpand(row)} group={row.original} groupType={groupType} />
            </div>
        );
    };

    function renderStudentGroupTable() {
        if (!studentStore) {
            return <Spinner animation="border" />;
        }
        return (
            <div className="students-group-main-container">
                <div className="header-students">
                    <h1 className="title-students">Student groups</h1>
                    <div className="d-flex gap-3">
                        {/* NOTE: removed as requested in the task EAI-5019*/}
                        {/* <Button onClick={() => fetchStudentGroups()} className="update-button">
                            <RotateRightIcon className="update-icon" />
                            <label>Update</label>
                        </Button> */}
                        <Button id="student-groups-create-group-button" variant="primary" onClick={openCreateModal}>
                            Create group
                        </Button>
                    </div>
                </div>
                <div className="search-bar">
                    <DebouncedInput type="text" value={searchBarValue} onChange={(value: string) => onSearchList(value)} placeholder="Search by Name or Description" className="search-input" />
                    <i className={"type icon icon-search search-icon-bar"} aria-hidden="true" data-testid="icon-search"></i>
                </div>
                {!dataQuery?.data ? (
                    <div className="text-center">
                        <Spinner variant="success" />
                    </div>
                ) : (
                    <StyledTable
                        defaultData={dataQuery?.data ?? []}
                        columns={columns}
                        getRowCanExpand={(value) => Boolean(value)}
                        onIsExpanded={(row) => {
                            return <div>{renderSubComponent({ row })}</div>;
                        }}
                        expanded={expanded}
                        setExpanded={setExpanded}
                        className="group-students-table"
                        noDataMessage="No student groups found"
                        currentPageNumber={pageIndex}
                        totalPagesNumber={getPageCount(dataQuery?.result?.totalCount ?? 0, pageSize)}
                        setCurrentNumber={(e) => onSetCurrentPageNumber(e, pageSize)}
                        setPageSize={(e) => setPagination({ pageIndex, pageSize: e })}
                        setSorting={(e) => {
                            setSorting(e);
                        }}
                        sorting={sorting}
                    />
                )}
            </div>
        );
    }

    return (
        <>
            <div className="student-groups-container">
                {renderStudentGroupTable()}
                {showCreateModal ? <StudentGroupCreateModal onSubmit={handleSave} handleCloseModal={handleModalClose} groupType={groupType} /> : null}
            </div>
        </>
    );
}
